import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { useEffect, useCallback } from 'react';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { retroColors } from './theme';

/**
 * Bottom navigation for the investor demo stepper.
 * Step dots, back/next buttons, keyboard navigation (arrow keys).
 */
export default function StepNavigation({
    currentStep,
    totalSteps,
    onStepChange,
    stepTitles = [],
}) {
    const canGoBack = currentStep > 0;
    const canGoForward = currentStep < totalSteps - 1;

    const goBack = useCallback(() => {
        if (canGoBack) onStepChange(currentStep - 1);
    }, [canGoBack, currentStep, onStepChange]);

    const goForward = useCallback(() => {
        if (canGoForward) onStepChange(currentStep + 1);
    }, [canGoForward, currentStep, onStepChange]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Don't capture if user is typing in an input
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

            if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                goBack();
            } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
                e.preventDefault();
                goForward();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [goBack, goForward]);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
            py: 2,
            px: 3,
            borderTop: `1px solid rgba(178, 140, 80, 0.1)`,
            bgcolor: 'rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
        }}>
            {/* Back button */}
            <Tooltip title="Previous (←)">
                <span>
                    <IconButton
                        onClick={goBack}
                        disabled={!canGoBack}
                        sx={{
                            color: canGoBack ? vortxColors.gold : retroColors.muted,
                            border: `1px solid ${canGoBack ? vortxColors.gold : 'rgba(136,136,136,0.3)'}`,
                            '&:hover': canGoBack ? { bgcolor: 'rgba(178, 140, 80, 0.15)' } : {},
                            '&.Mui-disabled': { color: 'rgba(136,136,136,0.3)' },
                        }}
                    >
                        <ArrowBack />
                    </IconButton>
                </span>
            </Tooltip>

            {/* Step dots */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                {Array.from({ length: totalSteps }, (_, i) => {
                    const isActive = i === currentStep;
                    const isPast = i < currentStep;
                    return (
                        <Tooltip key={i} title={stepTitles[i] || `Step ${i + 1}`}>
                            <Box
                                onClick={() => onStepChange(i)}
                                sx={{
                                    width: isActive ? 24 : 8,
                                    height: 8,
                                    borderRadius: 4,
                                    bgcolor: isActive
                                        ? vortxColors.gold
                                        : isPast
                                            ? 'rgba(178, 140, 80, 0.5)'
                                            : 'rgba(136,136,136,0.3)',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        bgcolor: isActive ? vortxColors.gold : 'rgba(178, 140, 80, 0.7)',
                                        transform: 'scale(1.2)',
                                    },
                                }}
                            />
                        </Tooltip>
                    );
                })}
            </Box>

            {/* Forward button */}
            <Tooltip title="Next (→)">
                <span>
                    <IconButton
                        onClick={goForward}
                        disabled={!canGoForward}
                        sx={{
                            color: canGoForward ? vortxColors.gold : retroColors.muted,
                            border: `1px solid ${canGoForward ? vortxColors.gold : 'rgba(136,136,136,0.3)'}`,
                            '&:hover': canGoForward ? { bgcolor: 'rgba(178, 140, 80, 0.15)' } : {},
                            '&.Mui-disabled': { color: 'rgba(136,136,136,0.3)' },
                        }}
                    >
                        <ArrowForward />
                    </IconButton>
                </span>
            </Tooltip>

            {/* Step title */}
            {stepTitles[currentStep] && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: retroColors.muted,
                    position: 'absolute',
                    right: 24,
                }}>
                    {stepTitles[currentStep]}
                </Typography>
            )}
        </Box>
    );
}
